import React, { useEffect, useState, useRef, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import "./UserManagement.css";
import api from "../utils/api";
import { validatePassword, PASSWORD_HINT } from "../utils/passwordRules";
import { menuItems } from "../components/Channel/ChannelSidebar";

// The panel stores sidebar access as raw keys (avgvidconsumption, tuneinout…).
// Show the name the page actually has in the sidebar; where two keys share a
// name (the demo variants) the first one keeps the plain name and the others
// get their key appended, so they stay distinguishable.
const SIDEBAR_LABELS = (() => {
  const seen = new Set();
  const map = {};
  menuItems.forEach((m) => {
    map[m.key] = seen.has(m.label) ? `${m.label} · ${m.key}` : m.label;
    seen.add(m.label);
  });
  return map;
})();
const sidebarLabel = (key) => SIDEBAR_LABELS[key] || key;
const sidebarMatches = (key, query) =>
  `${key} ${sidebarLabel(key)}`.toLowerCase().includes((query || "").toLowerCase());

// Field label with a live count and bulk actions, so a long selection can be
// read and cleared without scrolling through its chips.
function FieldHead({ label, count, onSelectAll, onClear }) {
  return (
    <div className="um-field-head">
      <label>{label}</label>
      <div className="um-field-tools">
        <span className="um-count">{count} selected</span>
        {onSelectAll && (
          <button type="button" className="um-link" onClick={onSelectAll}>
            Select all
          </button>
        )}
        <button type="button" className="um-link um-link-danger" onClick={onClear} disabled={!count}>
          Clear
        </button>
      </div>
    </div>
  );
}

function RoleSegmented({ value, onChange, canSuperadmin }) {
  const options = ["USER", "ADMIN"];
  if (canSuperadmin || value === "SUPERADMIN") options.push("SUPERADMIN");
  return (
    <div className="um-segmented" role="radiogroup" aria-label="Role">
      {options.map((o) => (
        <button
          type="button"
          key={o}
          role="radio"
          aria-checked={value === o}
          className={value === o ? "active" : ""}
          disabled={o === "SUPERADMIN" && !canSuperadmin}
          onClick={() => onChange(o)}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

// Simple Modal component
function Modal({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal" onClick={e => e.stopPropagation()}>
        <button className="um-modal-close" onClick={onClose}>×</button>
        {children}
      </div>
    </div>
  );
}

export default function UserManagement() {

  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [visibleUsers, setVisibleUsers] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [channelOptions, setChannelOptions] = useState([]);

  // Filter type and value state
  const [filterType, setFilterType] = useState("group"); // 'group' or 'email'
  const [filterValue, setFilterValue] = useState("");

  const [channelSearch, setChannelSearch] = useState("");
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [ytSearch, setYtSearch] = useState("");
  const [channelDropdown, setChannelDropdown] = useState(null);
  const [sidebarDropdown, setSidebarDropdown] = useState(false);

  const dropdownRef = useRef(null);

  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
    role: "USER",
    usergroup: "",
    channels: [],
    ytchannels: [],
    sidebaritems: []
  });
const [editOpen, setEditOpen] = useState(false);
// What the dialog opened with, so unsaved changes can be told apart from none.
const [initialUser, setInitialUser] = useState(null);
const [confirmDiscard, setConfirmDiscard] = useState(false);
const [selectedUser, setSelectedUser] = useState(null);
const [deleteConfirm, setDeleteConfirm] = useState({ open: false, userId: null });

/* ================= PASSWORD RESET ================= */
// One dialog, three stages: choose, confirm, then show the password once.
const emptyReset = {
  open: false,
  user: null,
  mode: "auto",          // auto | manual
  manual: "",
  mustChange: true,
  confirming: false,
  saving: false,
  error: "",
  result: null,          // { password } once the server has answered
  copied: false,
};
const [reset, setReset] = useState(emptyReset);

const openReset = (u) => setReset({ ...emptyReset, open: true, user: u });
const closeReset = () => setReset(emptyReset);

const submitReset = async () => {
  const target = reset.user;
  if (!target) return;

  if (reset.mode === "manual" && !validatePassword(reset.manual)) {
    setReset((r) => ({ ...r, error: PASSWORD_HINT, confirming: false }));
    return;
  }

  setReset((r) => ({ ...r, saving: true, error: "" }));
  try {
    const res = await api.post(`/users/${target._id}/reset-password`, {
      password: reset.mode === "manual" ? reset.manual : "",
      mustChange: reset.mustChange,
    });
    setReset((r) => ({
      ...r,
      saving: false,
      confirming: false,
      result: { password: res.data?.password || "" },
    }));
  } catch (err) {
    const status = err?.response?.status;
    setReset((r) => ({
      ...r,
      saving: false,
      confirming: false,
      error:
        status === 403
          ? "Only a superadmin can reset a password."
          : status === 400
          ? "The server rejected that password. " + PASSWORD_HINT
          : "Could not reset the password. Please try again.",
    }));
  }
};

const copyResetPassword = async () => {
  try {
    await navigator.clipboard.writeText(reset.result?.password || "");
    setReset((r) => ({ ...r, copied: true }));
  } catch {
    // Clipboard blocked; the password is on screen to be read off.
  }
};
const [expandedLists, setExpandedLists] = useState({});
/* ================= SAFE UNIQUE VALUES ================= */

const uniqueSidebar = useMemo(
  () => [...new Set(currentUser?.sidebaritems || [])],
  [currentUser]
);

const uniqueChannelOptions = useMemo(
  () => [...new Map(channelOptions.map(c => [c.id, c])).values()],
  [channelOptions]
);
const MAX_VISIBLE_TAGS = 6;

// Which lists are showing everything. Keyed per list, so opening the channels
// of one user does not open them everywhere.
const toggleList = (key) =>
  setExpandedLists(prev => ({ ...prev, [key]: !prev[key] }));

const renderMoreToggle = (key, hiddenCount, expanded, small) => {
  if (!expanded && hiddenCount <= 0) return null;
  return (
    <button
      type="button"
      className={`um-tag um-tag-more${small ? " small" : ""}`}
      onClick={() => toggleList(key)}
    >
      {expanded ? "show less" : `+${hiddenCount} more`}
    </button>
  );
};

const renderChannelChips = (ids, onRemove, listKey) => {
  const uniqueIds = [...new Set(ids || [])];
  const expanded = !!expandedLists[listKey];
  const visibleIds = expanded ? uniqueIds : uniqueIds.slice(0, MAX_VISIBLE_TAGS);
  const hiddenCount = uniqueIds.length - visibleIds.length;

  return (
    <>
      {visibleIds.map((id, idx) => {
        const ch = uniqueChannelOptions.find(c => c.id === id);
        return (
          <div key={`${id}-${idx}`} className="um-tag" title={ch ? id : ""}>
            {/* A channel the admin cannot see themselves still has to be
                listed, or removing it would be impossible. */}
            {ch ? ch.name : id}
            <span onClick={() => onRemove(id)}>×</span>
          </div>
        );
      })}
      {renderMoreToggle(listKey, hiddenCount, expanded, false)}
    </>
  );
};

const renderSidebarChips = (items, onRemove, listKey) => {
  const uniqueItems = [...new Set(items || [])];
  const expanded = !!expandedLists[listKey];
  const visibleItems = expanded ? uniqueItems : uniqueItems.slice(0, MAX_VISIBLE_TAGS);
  const hiddenCount = uniqueItems.length - visibleItems.length;

  return (
    <>
      {visibleItems.map((item, idx) => (
        <div key={`${item}-${idx}`} className="um-tag small" title={item}>
          {sidebarLabel(item)}
          <span onClick={() => onRemove(item)}>×</span>
        </div>
      ))}
      {renderMoreToggle(listKey, hiddenCount, expanded, true)}
    </>
  );
};
/* ================= FETCH USERS (WITH PANEL) ================= */
useEffect(() => {
  if (!currentUser?.panel) return;

  let cancelled = false;

  async function loadUsers() {
    try {
      const res = await api.get(
        `/users?panel=${currentUser.panel}`
      );

      if (cancelled) return;

      const data = res.data;

      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      if (cancelled) return;

      console.warn("loadUsers failed", err?.message);
      setUsers([]);
    }
  }

  loadUsers();

  return () => {
    cancelled = true;
  };
}, [currentUser]);

/* ================= RBAC ================= */
    useEffect(() => {

    if (!currentUser) return;

    const panel = currentUser?.panel || "";

    let filtered = (users || []).filter(u =>
        (u?.panel || "") === panel
    );

    if (currentUser?.role !== "SUPERADMIN") {
        filtered = filtered.filter(u =>
        (u?.usergroup || "") === (currentUser?.usergroup || "")
        );
    }

    if (currentUser?.role !== "SUPERADMIN") {
      filtered = filtered.filter(u =>
        (u?.email || "") !== (currentUser?.email || "")
      );
    }

    // Apply filter based on filterType
    if (filterValue.trim() !== "") {
      if (filterType === "group") {
        filtered = filtered.filter(u =>
          (u?.usergroup || "").toLowerCase().includes(filterValue.trim().toLowerCase())
        );
      } else if (filterType === "email") {
        filtered = filtered.filter(u =>
          (u?.email || "").toLowerCase().includes(filterValue.trim().toLowerCase())
        );
      }
    }
    setVisibleUsers(filtered);

    }, [users, currentUser, filterType, filterValue]);


  useEffect(() => {
    if (!formOpen && !editOpen) return;

    let cancelled = false;

    async function loadChannels() {
      try {
        // 🔥 SUPERADMIN
        if (currentUser?.role === "SUPERADMIN") {
          const res = await api.get("/allci");

          if (cancelled) return;

          const data = res.data;

          setChannelOptions(
            (data || []).map((c) => ({
              id: String(c?.id || c?._id || c?.i || ""),
              name: c?.channelName || c?.n || "",
            }))
          );

          return;
        }

        // 🔥 NORMAL USER / ADMIN
        const ids = currentUser?.channels || [];
        if (!ids.length) {
          setChannelOptions([]);
          return;
        }

        const res = await api.get(`/ci?i=${ids.join("-")}`);

        if (cancelled) return;

        const data = res.data;

        setChannelOptions(
          (data || []).map((c) => ({
            id: String(c?.i || ""),
            name: c?.n || "",
          }))
        );

      } catch (err) {
        if (cancelled) return;

        console.warn("loadChannels failed", err?.message);
        setChannelOptions([]);
      }
    }

    loadChannels();

    return () => {
      cancelled = true;
    };
  }, [formOpen, editOpen, currentUser]);

  useEffect(() => {
    const handler = e => {
      // Close dropdowns if click is outside any .um-dropdown or .um-tags
      const dropdowns = document.querySelectorAll('.um-dropdown, .um-tags');
      let inside = false;
      dropdowns.forEach(node => {
        if (node.contains(e.target)) inside = true;
      });
      if (!inside) {
        setChannelDropdown(null);
        setSidebarDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

/* ================= CREATE ================= */
const [formError, setFormError] = useState("");
  const handleCreate = async () => {
    // Validation
    if (!newUser.email || !newUser.password) {
      setFormError("Email and password cannot be empty.");
      return;
    }

    if (!newUser.email.includes("@")) {
      setFormError("Email must include '@'.");
      return;
    }

    const pw = newUser.password;
    if (!(/[A-Z]/.test(pw) || /[0-9]/.test(pw) || /[^A-Za-z0-9]/.test(pw))) {
      setFormError("Password must include a number, symbol, or uppercase letter.");
      return;
    }

    setFormError("");

    const payload = {
      username: newUser.username,
      email: newUser.email,
      password: newUser.password,
      role: newUser.role,
      usergroup:
        currentUser?.role === "ADMIN"
          ? currentUser?.usergroup
          : newUser.usergroup,
      channels: [...new Set(newUser.channels)],
      ytchannels:
      currentUser?.role === "SUPERADMIN"
        ? [...new Set(newUser.ytchannels)]
        : [],
      sidebaritems: [...new Set(newUser.sidebaritems)],
      panel: currentUser?.panel,
    };

    try {
      await api.post("/users", payload);

      setFormOpen(false);
      window.location.reload(); // aynen koruduk

    } catch (err) {
      console.error(err);
      setFormError("Failed to create user.");
    }
  };

  // 🔥 EDIT (unchanged - sadece state)
  const handleEdit = (u) => {
    const next = {
      _id: u._id,
      email: u.email,
      role: u.role,
      usergroup: u.usergroup,
      channels: u.channels || [],
      ytchannels: u.ytchannels || [],
      sidebaritems: u.sidebaritems || [],
      tokenminutes: u.tokenminutes || "",
      ttvoverview: !!u.ttvoverview,
      ttvallowance: !!u.ttvallowance,
    };
    setSelectedUser(next);
    setInitialUser(next);
    setConfirmDiscard(false);
    setEditOpen(true);
  };

  // Order-insensitive, so re-adding the same chips in another order is not a change.
  const comparable = (u) =>
    JSON.stringify({
      role: u?.role || "",
      usergroup: u?.usergroup || "",
      channels: [...new Set(u?.channels || [])].sort(),
      ytchannels: [...new Set(u?.ytchannels || [])].sort(),
      sidebaritems: [...new Set(u?.sidebaritems || [])].sort(),
      tokenminutes: String(u?.tokenminutes ?? ""),
      ttvoverview: !!u?.ttvoverview,
      ttvallowance: !!u?.ttvallowance,
    });
  const isDirty =
    !!selectedUser && !!initialUser && comparable(selectedUser) !== comparable(initialUser);

  const closeEdit = () => {
    setEditOpen(false);
    setConfirmDiscard(false);
  };
  const requestCloseEdit = () => {
    if (isDirty) setConfirmDiscard(true);
    else closeEdit();
  };

  // 🔥 UPDATE
  const handleUpdate = async () => {
    const payload = {
      role: selectedUser.role,
      usergroup: selectedUser.usergroup,
      channels: [...new Set(selectedUser.channels)],
      ytchannels:
      currentUser?.role === "SUPERADMIN"
        ? [...new Set(selectedUser.ytchannels || [])]
        : undefined,
      sidebaritems: [...new Set(selectedUser.sidebaritems)],
      tokenminutes:
        currentUser?.role === "SUPERADMIN"
          ? Number(selectedUser.tokenminutes) || 0
          : undefined,
      ttvoverview:
        currentUser?.role === "SUPERADMIN"
          ? !!selectedUser.ttvoverview
          : undefined,
      ttvallowance:
        currentUser?.role === "SUPERADMIN"
          ? !!selectedUser.ttvallowance
          : undefined,
    };

    try {
      await api.put(`/users/${selectedUser._id}`, payload);

      setEditOpen(false);
      window.location.reload();

    } catch (err) {
      console.error(err);
    }
  };

  // 🔥 DELETE (modal trigger unchanged)
  const handleDelete = (id) => {
    setDeleteConfirm({ open: true, userId: id });
  };

  // 🔥 CONFIRM DELETE
  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${deleteConfirm.userId}`);

      setDeleteConfirm({ open: false, userId: null });
      window.location.reload();

    } catch (err) {
      console.error(err);
    }
  };

return (
<div className="profile-page um-page">
<div className="profile-card um-card">

<div className="um-header">
  <h2>User Management</h2>
  <button className="um-create-btn" onClick={() => setFormOpen(!formOpen)}>
    + Create User
  </button>
</div>

{/* Group filter input */}
<div className="um-filter-row">
  <label htmlFor="filter-type">Filter by:</label>
  <select
    id="filter-type"
    className="um-filter-select"
    value={filterType}
    onChange={e => {
      setFilterType(e.target.value);
      setFilterValue("");
    }}
  >
    <option value="group">Group</option>
    <option value="email">Email</option>
  </select>
  <input
    className="um-filter-input"
    type="text"
    placeholder={filterType === "group" ? "Type group name..." : "Type email..."}
    value={filterValue}
    onChange={e => setFilterValue(e.target.value)}
  />
</div>

<Modal open={formOpen} onClose={() => {
  setFormOpen(false);
  setChannelDropdown(null);
  setSidebarDropdown(null);
  setNewUser({
    username: "",
    email: "",
    password: "",
    role: "USER",
    usergroup: "",
    channels: [],
    ytchannels: [],
    sidebaritems: []
  });
  setChannelSearch("");
  setSidebarSearch("");
  setFormError("");
  setYtSearch("");
}}>
  <div className="um-create" ref={dropdownRef}>
    <h3 style={{marginBottom: 16}}>Create User</h3>
    <div className="um-field">
      <label>Username</label>
      <input
        value={newUser.username}
        onChange={e => setNewUser(prev => ({ ...prev, username: e.target.value }))}
      />
    </div>
    <div className="um-field">
      <label>Email</label>
      <input
        value={newUser.email}
        onChange={e => setNewUser(prev => ({ ...prev, email: e.target.value }))}
      />
    </div>
    <div className="um-field">
      <label>Password</label>
      <input
        type="password"
        value={newUser.password}
        onChange={e => setNewUser(prev => ({ ...prev, password: e.target.value }))}
      />
    </div>
    <div className="um-field">
      <label>Role</label>
        <RoleSegmented
          value={newUser.role}
          onChange={(role) => setNewUser(prev => ({ ...prev, role }))}
          canSuperadmin={currentUser?.role === "SUPERADMIN"}
        />
    </div>
    <div className="um-field" style={{ gridColumn: 'span 2' }}>
      <label>User Group</label>
      {currentUser?.role === "SUPERADMIN" ? (
        <input
          value={newUser.usergroup}
          onChange={e => setNewUser(prev => ({ ...prev, usergroup: e.target.value }))}
        />
      ) : (
        <input value={currentUser?.usergroup || ""} disabled />
      )}
    </div>
    {/* ===== CHANNEL FIELD ===== */}
    <div className="um-field">
      <FieldHead
        label="Channels"
        count={new Set(newUser.channels).size}
        onSelectAll={currentUser?.role === "SUPERADMIN" ? () => setNewUser(prev => ({ ...prev, channels: uniqueChannelOptions.map(c => c.id) })) : undefined}
        onClear={() => setNewUser(prev => ({ ...prev, channels: [] }))}
      />
      <div className="um-multi">
        <div className="um-tags">
          {renderChannelChips(newUser.channels, id =>
            setNewUser(prev => ({
              ...prev,
              channels: prev.channels.filter(c => c !== id)
            }))
          , "new-channels")}
          <input
            className="um-search"
            placeholder="Search channel..."
            value={channelSearch}
            onFocus={() => setChannelDropdown("channels")}
            onChange={e => setChannelSearch(e.target.value)}
            style={{minWidth: '120px', flex: '1 1 120px'}}
          />
        </div>
        {channelDropdown === "channels" && (
          <div className="um-dropdown">
           {currentUser?.role === "SUPERADMIN" && (
              <>
                <div
                  className="um-option"
                  style={{ fontWeight: 600, borderBottom: "1px solid #eee" }}
                  onClick={() => {
                    setNewUser(prev => ({
                      ...prev,
                      channels: uniqueChannelOptions.map(c => c.id)
                    }));
                    setChannelDropdown(null);
                  }}
                >
                  ✅ Select All Channels
                </div>

                {currentUser?.channels?.length > 0 && (
                  <div
                    className="um-option"
                    style={{ fontWeight: 500 }}
                    onClick={() => {
                      setNewUser(prev => ({
                        ...prev,
                        channels: currentUser.channels
                      }));
                      setChannelDropdown(null);
                    }}
                  >
                    ⭐ Select My Channels
                  </div>
                )}
              </>
            )}
            <div
              className="um-option"
              style={{ color: "#d32f2f" }}
              onClick={() => {
                console.log("CLEAR CREATE");
                setNewUser(prev => ({
                  ...prev,
                  channels: []
                }));
                  setChannelSearch("");        // 🔥 önemli
                  setChannelDropdown(null); 
              }}
            >
              ❌ Clear All
            </div>
            {uniqueChannelOptions
              .filter(c =>
                (c?.name || "")
                  .toLowerCase()
                  .includes((channelSearch || "").toLowerCase())
              )
              .map((c, idx) => (
                <div
                  key={`${c.id}-${idx}`}
                  className="um-option"
                  onClick={() => {
                    setNewUser(prev => {
                      if (prev.channels.includes(c.id)) return prev;
                      return {
                        ...prev,
                        channels: [...prev.channels, c.id]
                      };
                    });
                    setChannelSearch("");
                    setChannelDropdown(null);
                  }}
                >
                  {c?.name || "unknown"}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
    {currentUser?.role === "SUPERADMIN" && (
      <div className="um-field">
        <FieldHead
          label="YT Channels"
          count={new Set(newUser.ytchannels).size}
          onSelectAll={() => setNewUser(prev => ({ ...prev, ytchannels: uniqueChannelOptions.map(c => c.id) }))}
          onClear={() => setNewUser(prev => ({ ...prev, ytchannels: [] }))}
        />

        <div className="um-multi">
          <div className="um-tags">
            {renderChannelChips(newUser.ytchannels, id =>
              setNewUser(prev => ({
                ...prev,
                ytchannels: prev.ytchannels.filter(x => x !== id)
              }))
            , "new-ytchannels")}

            <input
              className="um-search"
              placeholder="Search YT channel..."
              value={ytSearch}
              onFocus={() => setChannelDropdown("yt")}
              onChange={e => setYtSearch(e.target.value)}
            />
          </div>

                {channelDropdown === "yt" && (
                  <div className="um-dropdown">
                    {uniqueChannelOptions
                      .filter(c =>
                        (c?.name || "")
                          .toLowerCase()
                          .includes((ytSearch || "").toLowerCase())
                      )
                      .map((c, idx) => (
                <div
                  key={`${c.id}-${idx}`}
                  className="um-option"
                  onClick={() => {
                    setNewUser(prev => {
                      if (prev.ytchannels.includes(c.id)) return prev;
                      return {
                        ...prev,
                        ytchannels: [...prev.ytchannels, c.id]
                      };
                    });
                    setYtSearch("");
                    setChannelDropdown(null);
                  }}
                >
                  {c.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )}
    {/* ===== SIDEBAR FIELD ===== */}
    <div className="um-field um-field-full">
      <FieldHead
        label="Sidebar"
        count={new Set(newUser.sidebaritems).size}
        onSelectAll={() => setNewUser(prev => ({ ...prev, sidebaritems: [...uniqueSidebar] }))}
        onClear={() => setNewUser(prev => ({ ...prev, sidebaritems: [] }))}
      />
      <div className="um-multi">
        <div className="um-tags">
          {renderSidebarChips(newUser.sidebaritems, item =>
            setNewUser(prev => ({
              ...prev,
              sidebaritems: prev.sidebaritems.filter(x => x !== item)
            }))
          , "new-sidebar")}
          <input
            className="um-search"
            placeholder="Search sidebar..."
            value={sidebarSearch}
            onFocus={() => setSidebarDropdown(true)}
            onChange={e => setSidebarSearch(e.target.value)}
            style={{minWidth: '120px', flex: '1 1 120px'}}
          />
        </div>
        {sidebarDropdown && (
          <div className="um-dropdown">
            {uniqueSidebar
              .filter(i => sidebarMatches(i, sidebarSearch))
              .map((i, idx) => (
                <div
                  key={`${i}-${idx}`}
                  className="um-option"
                  onClick={() => {
                    setNewUser(prev => {
                      if (prev.sidebaritems.includes(i)) return prev;
                      return {
                        ...prev,
                        sidebaritems: [...prev.sidebaritems, i]
                      };
                    });
                    setSidebarSearch("");
                  }}
                >
                  {sidebarLabel(i)}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
    {formError && (
      <div style={{gridColumn: 'span 4', color: '#d32f2f', marginBottom: 8, fontWeight: 500}}>
        {formError}
      </div>
    )}
    <div className="um-modal-footer">
      <button className="um-create-btn um-modal-save" onClick={handleCreate}>
        Save
      </button>
    </div>
  </div>
</Modal>

<Modal open={editOpen && selectedUser} onClose={requestCloseEdit}>
  <div className="um-create">
    <h3 style={{marginBottom: selectedUser?.email ? 4 : 16}}>Edit User</h3>
    {selectedUser?.email && (
      <div className="um-edit-subtitle">{selectedUser.email}</div>
    )}
    <div className="um-field">
      <label>Role</label>
        <RoleSegmented
          value={selectedUser?.role || "USER"}
          onChange={(role) => setSelectedUser(prev => ({ ...prev, role }))}
          canSuperadmin={currentUser?.role === "SUPERADMIN"}
        />
    </div>
    <div className="um-field">
      <label>User Group</label>
      <input
        value={selectedUser?.usergroup || ""}
        onChange={e =>
          setSelectedUser(prev => ({ ...prev, usergroup: e.target.value }))
        }
      />
    </div>
    {/* ===== CHANNEL FIELD ===== */}
    <div className="um-field">
      <FieldHead
        label="Channels"
        count={new Set(selectedUser?.channels || []).size}
        onSelectAll={currentUser?.role === "SUPERADMIN" ? () => setSelectedUser(prev => ({ ...prev, channels: uniqueChannelOptions.map(c => c.id) })) : undefined}
        onClear={() => setSelectedUser(prev => ({ ...prev, channels: [] }))}
      />
      <div className="um-multi">
        <div className="um-tags">
          {renderChannelChips(selectedUser?.channels || [], id =>
            setSelectedUser(prev => ({
              ...prev,
              channels: prev.channels.filter(c => c !== id)
            }))
          , "edit-channels")}
          <input
            className="um-search"
            placeholder="Search channel..."
            value={channelSearch}
            onFocus={() => setChannelDropdown("channels")}
            onChange={e => setChannelSearch(e.target.value)}
            style={{minWidth: '120px', flex: '1 1 120px'}}
          />
        </div>
        {channelDropdown === "channels" && (
          <div className="um-dropdown">

            {currentUser?.role === "SUPERADMIN" && (
              <>
                <div
                  className="um-option"
                  style={{ fontWeight: 600, borderBottom: "1px solid #eee" }}
                  onClick={() => {
                    setSelectedUser(prev => ({
                      ...prev,
                      channels: uniqueChannelOptions.map(c => c.id)
                    }));
                    setChannelDropdown(null);
                  }}
                >
                  ✅ Select All Channels
                </div>

                {currentUser?.channels?.length > 0 && (
                  <div
                    className="um-option"
                    style={{ fontWeight: 500 }}
                    onClick={() => {
                      setSelectedUser(prev => ({
                        ...prev,
                        channels: currentUser.channels
                      }));
                      setChannelDropdown(null);
                    }}
                  >
                    ⭐ Select My Channels
                  </div>
                )}
              </>
            )}

            <div
              className="um-option"
              style={{ color: "#d32f2f" }}
              onClick={() => {
                console.log("CLEAR EDIT");

                setSelectedUser(prev => ({
                  ...prev,
                  channels: []
                }));

                setChannelSearch("");
                setChannelDropdown(false);
              }}
            >
              ❌ Clear All
            </div>

            {uniqueChannelOptions
              .filter(c =>
                (c?.name || "")
                  .toLowerCase()
                  .includes((channelSearch || "").toLowerCase())
              )
              .map((c, idx) => (
                <div
                  key={`${c.id}-${idx}`}
                  className="um-option"
                  onClick={() => {
                    setSelectedUser(prev => {
                      if (prev.channels.includes(c.id)) return prev;
                      return {
                        ...prev,
                        channels: [...prev.channels, c.id]
                      };
                    });
                    setChannelSearch("");
                    setChannelDropdown(false);
                  }}
                >
                  {c?.name || "unknown"}
                </div>
              ))}

          </div>
        )}
      </div>
    </div>
    {currentUser?.role === "SUPERADMIN" && (
      <div className="um-field">
        <FieldHead
          label="YT Channels"
          count={new Set(selectedUser?.ytchannels || []).size}
          onSelectAll={() => setSelectedUser(prev => ({ ...prev, ytchannels: uniqueChannelOptions.map(c => c.id) }))}
          onClear={() => setSelectedUser(prev => ({ ...prev, ytchannels: [] }))}
        />

        <div className="um-multi">
          <div className="um-tags">
            {renderChannelChips(selectedUser?.ytchannels || [], id =>
              setSelectedUser(prev => ({
                ...prev,
                ytchannels: prev.ytchannels.filter(x => x !== id)
              }))
            , "edit-ytchannels")}

            <input
              className="um-search"
              placeholder="Search YT channel..."
              value={ytSearch}
              onFocus={() => setChannelDropdown("yt")}
              onChange={e => setYtSearch(e.target.value)}
            />
          </div>

                {channelDropdown === "yt" && (
                  <div className="um-dropdown">
                    {uniqueChannelOptions
                      .filter(c =>
                        (c?.name || "")
                          .toLowerCase()
                          .includes((ytSearch || "").toLowerCase())
                      )
                      .map((c, idx) => (
                <div
                  key={`${c.id}-${idx}`}
                  className="um-option"
                  onClick={() => {
                    setSelectedUser(prev => {
                      if (prev.ytchannels.includes(c.id)) return prev;
                      return {
                        ...prev,
                        ytchannels: [...prev.ytchannels, c.id]
                      };
                    });
                    setYtSearch("");
                    setChannelDropdown(null);
                  }}
                >
                  {c.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )}
    {currentUser?.role === "SUPERADMIN" && (
      <div className="um-field">
        <label>Session length</label>
        <input
          type="number"
          min="5"
          max="43200"
          placeholder="Default"
          value={selectedUser?.tokenminutes ?? ""}
          onChange={(e) =>
            setSelectedUser((prev) => ({ ...prev, tokenminutes: e.target.value }))
          }
        />
        <small className="um-field-hint">
          Minutes before this user has to sign in again. Leave blank for the
          default.
        </small>
      </div>
    )}
    {currentUser?.role === "SUPERADMIN" && (
      <div className="um-field">
        <label>TMTV</label>
        <div className="um-toggle-list">
          <div className="um-toggle-item">
            <label className="um-toggle">
              <input
                type="checkbox"
                checked={!!selectedUser?.ttvoverview}
                onChange={(e) =>
                  setSelectedUser((prev) => ({ ...prev, ttvoverview: e.target.checked }))
                }
              />
              <span className="um-toggle-slider" />
            </label>
            <div className="um-toggle-copy">
              <span className="um-toggle-text">TMTV Overview</span>
              <small className="um-field-hint">
                TMTV value next to "active TV" on Overview.
              </small>
            </div>
          </div>
          <div className="um-toggle-item">
            <label className="um-toggle">
              <input
                type="checkbox"
                checked={!!selectedUser?.ttvallowance}
                onChange={(e) =>
                  setSelectedUser((prev) => ({ ...prev, ttvallowance: e.target.checked }))
                }
              />
              <span className="um-toggle-slider" />
            </label>
            <div className="um-toggle-copy">
              <span className="um-toggle-text">TMTV Charts</span>
              <small className="um-field-hint">
                TMTV line on Channel Grid and TMTV mode on Avg Active TV.
              </small>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* ===== SIDEBAR FIELD ===== */}
    <div className="um-field um-field-full">
      <FieldHead
        label="Sidebar"
        count={new Set(selectedUser?.sidebaritems || []).size}
        onSelectAll={() => setSelectedUser(prev => ({ ...prev, sidebaritems: [...uniqueSidebar] }))}
        onClear={() => setSelectedUser(prev => ({ ...prev, sidebaritems: [] }))}
      />
      <div className="um-multi">
        <div className="um-tags">
          {renderSidebarChips(selectedUser?.sidebaritems || [], item =>
            setSelectedUser(prev => ({
              ...prev,
              sidebaritems: prev.sidebaritems.filter(x => x !== item)
            }))
          , "edit-sidebar")}
          <input
            className="um-search"
            placeholder="Search sidebar..."
            value={sidebarSearch}
            onFocus={() => setSidebarDropdown(true)}
            onChange={e => setSidebarSearch(e.target.value)}
            style={{minWidth: '120px', flex: '1 1 120px'}}
          />
        </div>
        {sidebarDropdown && (
          <div className="um-dropdown">
            {uniqueSidebar
              .filter(i => sidebarMatches(i, sidebarSearch))
              .map((i, idx) => (
                <div
                  key={`${i}-${idx}`}
                  className="um-option"
                  onClick={() => {
                    setSelectedUser(prev => {
                      if (prev.sidebaritems.includes(i)) return prev;
                      return {
                        ...prev,
                        sidebaritems: [...prev.sidebaritems, i]
                      };
                    });
                    setSidebarSearch("");
                    setSidebarDropdown(false);
                  }}
                >
                  {sidebarLabel(i)}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
    <div className="um-modal-footer">
      {confirmDiscard ? (
        <div className="um-discard-bar">
          <span>You have unsaved changes.</span>
          <div className="um-discard-actions">
            <button type="button" className="um-btn" onClick={() => setConfirmDiscard(false)}>
              Keep editing
            </button>
            <button type="button" className="um-btn danger" onClick={closeEdit}>
              Discard
            </button>
          </div>
        </div>
      ) : (
        <button
          className="um-create-btn um-modal-save"
          onClick={handleUpdate}
          disabled={!isDirty}
          title={isDirty ? "" : "No changes to save"}
        >
          {isDirty ? "Update" : "No changes"}
        </button>
      )}
    </div>
  </div>
</Modal>
{reset.open && (
  <Modal open={true} onClose={closeReset}>
    <div className="um-reset">
      {/* Stage three: the password exists now, and this is the only time it
          is readable. Closing the dialog is the point of no return. */}
      {reset.result ? (
        <>
          <div className="um-reset-title">Password reset</div>
          <p className="um-reset-lead">
            Copy it now — it cannot be shown again. Send it to{" "}
            <strong>{reset.user?.email}</strong> yourself.
          </p>

          <div className="um-reset-password">
            <code>{reset.result.password}</code>
            <button type="button" className="um-btn" onClick={copyResetPassword}>
              {reset.copied ? "Copied" : "Copy"}
            </button>
          </div>

          <p className="um-reset-note">
            {reset.mustChange
              ? "They will have to choose their own password before the panel opens."
              : "They can keep using this password; you did not require a change."}
          </p>

          <div className="um-reset-actions">
            <button type="button" className="um-btn primary" onClick={closeReset}>
              Done
            </button>
          </div>
        </>
      ) : reset.confirming ? (
        <>
          <div className="um-reset-title">Reset this password?</div>
          <p className="um-reset-lead">
            <strong>{reset.user?.email}</strong> will be signed out of nothing —
            but their current password stops working immediately.
          </p>
          {reset.error && <div className="um-reset-error">{reset.error}</div>}
          <div className="um-reset-actions">
            <button
              type="button"
              className="um-btn"
              onClick={() => setReset((r) => ({ ...r, confirming: false }))}
              disabled={reset.saving}
            >
              Back
            </button>
            <button
              type="button"
              className="um-btn danger"
              onClick={submitReset}
              disabled={reset.saving}
            >
              {reset.saving ? "Resetting…" : "Yes, reset it"}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="um-reset-title">Reset password</div>
          <p className="um-reset-lead">
            For <strong>{reset.user?.email}</strong>
          </p>

          <label className="um-reset-choice">
            <input
              type="radio"
              checked={reset.mode === "auto"}
              onChange={() => setReset((r) => ({ ...r, mode: "auto", error: "" }))}
            />
            <span>
              Generate one
              <small>14 characters, no lookalike letters</small>
            </span>
          </label>

          <label className="um-reset-choice">
            <input
              type="radio"
              checked={reset.mode === "manual"}
              onChange={() => setReset((r) => ({ ...r, mode: "manual", error: "" }))}
            />
            <span>
              Type one myself
              <small>{PASSWORD_HINT}</small>
            </span>
          </label>

          {reset.mode === "manual" && (
            <input
              className="um-reset-input"
              type="text"
              placeholder="New password"
              value={reset.manual}
              onChange={(e) =>
                setReset((r) => ({ ...r, manual: e.target.value, error: "" }))
              }
            />
          )}

          <label className="um-reset-check">
            <input
              type="checkbox"
              checked={reset.mustChange}
              onChange={(e) =>
                setReset((r) => ({ ...r, mustChange: e.target.checked }))
              }
            />
            <span>
              Make them choose their own on next sign-in
              <small>The panel stays closed until they do</small>
            </span>
          </label>

          {reset.error && <div className="um-reset-error">{reset.error}</div>}

          <div className="um-reset-actions">
            <button type="button" className="um-btn" onClick={closeReset}>
              Cancel
            </button>
            <button
              type="button"
              className="um-btn primary"
              onClick={() => setReset((r) => ({ ...r, confirming: true, error: "" }))}
            >
              Continue
            </button>
          </div>
        </>
      )}
    </div>
  </Modal>
)}
{deleteConfirm.open && (
  <Modal open={true} onClose={() => setDeleteConfirm({ open: false, userId: null })}>
    <div style={{padding: '24px 0', textAlign: 'center'}}>
      <div style={{fontSize: '18px', fontWeight: 600, marginBottom: 18}}>Delete User?</div>
      <div style={{color: '#d32f2f', marginBottom: 24}}>Are you sure you want to delete this user? This action cannot be undone.</div>
      <button className="um-create-btn um-delete-btn" style={{marginRight: 12}} onClick={confirmDelete}>Delete</button>
      <button className="um-create-btn" style={{background:'#e5e7eb', color:'#222'}} onClick={() => setDeleteConfirm({ open: false, userId: null })}>Cancel</button>
    </div>
  </Modal>
)}

<div className="um-table-wrapper">
  <table className="um-table">
    <thead>
      <tr>
        <th>Email</th>
        <th>Role</th>
        <th>Group</th>
        <th style={{minWidth: 120}}>Actions</th>
      </tr>
    </thead>
    <tbody>
      {(visibleUsers || []).map(u => (
        <tr key={u?._id}>
          <td>{u?.email || "-"}</td>
          <td>
            <span className={`um-role ${(u?.role || "").toLowerCase()}`}>
              {u?.role || "-"}
            </span>
          </td>
          <td>
            <span className="um-group">
              {u?.usergroup || "-"}
            </span>
          </td>
          <td>
            <button className="um-action-btn" onClick={() => handleEdit(u)}>
              Edit
            </button>
            {currentUser?.role === "SUPERADMIN" && (
              <button
                className="um-action-btn um-reset-btn"
                onClick={() => openReset(u)}
              >
                Reset password
              </button>
            )}
            <button
              className="um-action-btn um-delete-btn"
              onClick={() => handleDelete(u._id)}
            >
              Delete
            </button>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>

</div>
</div>
);
}
